/* Phase 6b — Customer / Shop infrastructure: api client, contexts, helpers. */
import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;
export const CUSTOMER_TOKEN_KEY = "scienceai_customer_token";
export const CURRENCY_KEY = "scienceai_shop_currency";
export const COUNTRY_KEY = "scienceai_shop_country";

/* ---------- Axios instance (customer-only). Sends bearer only on /shop/* ---------- */
export const customerApi = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});
customerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
  const url = config.url || "";
  if (token && url.startsWith("/shop")) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* ---------- Customer auth context ---------- */
const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(CUSTOMER_TOKEN_KEY));
  const [customer, setCustomer] = useState(null);
  const [ready, setReady] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    setToken(null);
    setCustomer(null);
  }, []);

  useEffect(() => {
    let on = true;
    if (!token) { setCustomer(null); setReady(true); return; }
    customerApi.get("/shop/auth/me")
      .then(({ data }) => { if (on) { setCustomer(data); setReady(true); } })
      .catch(() => { if (on) { localStorage.removeItem(CUSTOMER_TOKEN_KEY); setToken(null); setCustomer(null); setReady(true); } });
    return () => { on = false; };
  }, [token]);

  const requestOtp = useCallback(async (email) => {
    const { data } = await customerApi.post("/shop/auth/request-otp", { email });
    return data;
  }, []);

  const verifyOtp = useCallback(async (email, code) => {
    const { data } = await customerApi.post("/shop/auth/verify-otp", { email, code });
    localStorage.setItem(CUSTOMER_TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    setCustomer(data.customer);
    return data.customer;
  }, []);

  const refresh = useCallback(async () => {
    if (!token) return null;
    const { data } = await customerApi.get("/shop/auth/me");
    setCustomer(data);
    return data;
  }, [token]);

  const value = useMemo(() => ({ token, customer, requestOtp, verifyOtp, logout, refresh, ready }),
    [token, customer, requestOtp, verifyOtp, logout, refresh, ready]);
  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomer() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error("useCustomer must be inside CustomerAuthProvider");
  return ctx;
}

/* ---------- Currency context ---------- */
const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => localStorage.getItem(CURRENCY_KEY) || "USD");
  const [supported, setSupported] = useState(["USD", "INR", "EUR", "GBP"]);
  const [devMode, setDevMode] = useState(false);

  useEffect(() => {
    customerApi.get("/site-content").catch(() => {}); // warm cache; actual settings come from /shop/products meta — fall back to defaults
  }, []);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c);
    localStorage.setItem(CURRENCY_KEY, c);
  }, []);

  return <CurrencyContext.Provider value={{ currency, setCurrency, supported, setSupported, devMode, setDevMode }}>
    {children}
  </CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be inside CurrencyProvider");
  return ctx;
}

/* ---------- Cart context (count badge) ---------- */
const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { token, customer } = useCustomer();
  const [count, setCount] = useState(0);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(async () => {
    if (!token || !customer) { setCount(0); return; }
    try {
      const { data } = await customerApi.get("/shop/cart?currency=USD");
      setCount((data?.items || []).reduce((n, l) => n + (l.qty || 0), 0));
    } catch { setCount(0); }
  }, [token, customer]);

  useEffect(() => { refresh(); }, [refresh, version]);

  const bumpVersion = useCallback(() => setVersion((v) => v + 1), []);

  return <CartContext.Provider value={{ count, refresh, bumpVersion }}>
    {children}
  </CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
}

/* ---------- Helpers ---------- */
export function formatPrice(amount, currency = "USD") {
  if (amount == null || isNaN(Number(amount))) return "";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(amount));
  } catch {
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
}

export function resolveImg(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${url}`;
  return url; // /images/shop/...
}

export const COUNTRIES = [
  { code: "US", name: "United States", states: true },
  { code: "IN", name: "India", states: true },
  { code: "GB", name: "United Kingdom" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "JP", name: "Japan" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "BR", name: "Brazil" },
  { code: "XX", name: "Other (specify)" },
];

export function StatusBadge({ status }) {
  const m = {
    pending: "bg-amber-100 text-amber-800",
    paid: "bg-emerald-100 text-emerald-800",
    fulfilled: "bg-sky-100 text-sky-800",
    shipped: "bg-indigo-100 text-indigo-800",
    delivered: "bg-emerald-100 text-emerald-800",
    refunded: "bg-neutral-200 text-neutral-700",
    cancelled: "bg-rose-100 text-rose-700",
    failed: "bg-rose-100 text-rose-700",
  };
  const cls = m[status] || "bg-neutral-100 text-neutral-700";
  return <span className={`inline-flex items-center rounded-full px-2 h-5 text-[11px] font-medium ${cls}`}>{status}</span>;
}
